import { useMutation, useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import { LogLinearScaleSelector, type AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import { getLabelForBookmarkTooltip } from "components/company/valuation/IndicatorSnapshot";
import valuationIndicatorFormatValue, {
  valuationIndicatorsFormatAsPercentage,
} from "components/company/valuation/utils/valuationIndicatorFormat";
import { verifyIndicatorIsValuationRatio } from "components/company/valuation/utils/verifyIndicatorIsValuationRatios";
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import ExpandableParagraph from "components/ui/ExpandableParagraph";
import { Separator } from "components/ui/Separator";
import { SimpleTextTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import compareDesc from "date-fns/compareDesc";
import getYear from "date-fns/getYear";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import type { SeriesColumnOptions, SeriesLineOptions } from "highcharts";
import ToggleBookmarkForIndicatorMutation from "lib/graphql/mutations/settings/ToggleBookmarkForIndicatorMutation";
import type {
  IToggleBookmarkForIndicatorMutation,
  IToggleBookmarkForIndicatorMutationVariables,
} from "lib/graphql/mutations/settings/__generated__/ToggleBookmarkForIndicatorMutation.generated";
import useSelfie from "lib/hooks/useSelfie";
import ValuationIndicatorHistoryForTickerSymbolsQuery from "lib/queries/ValuationIndicatorHistoryForTickerSymbolsQuery";
import type {
  IValuationIndicatorHistoryForTickerSymbolsQuery,
  IValuationIndicatorHistoryForTickerSymbolsQueryVariables,
} from "lib/queries/__generated__/ValuationIndicatorHistoryForTickerSymbolsQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { BookmarkIcon, GraduationCapIcon } from "lucide-react";
import { useRouter } from "next/router";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { ISecurityCountry, IValuationIndicatorVerbiageLanguage } from "types/graphqlTypes";
import { IValuationIndicator, IValuationPeriod } from "types/graphqlTypes";
import { useLocalStorage } from "usehooks-ts";

// Remembers the last plot type/scale picked, so it's the default the next time any valuation chart is opened
const PLOT_TYPE_STORAGE_KEY = "fundamentei:valuation:plotType";
const AXIS_SCALE_TYPE_STORAGE_KEY = "fundamentei:valuation:axisScaleType";

interface IValuationHistoryProps {
  verbiageLanguage: IValuationIndicatorVerbiageLanguage;
  securityCountry: ISecurityCountry;
  tickerSymbol: string;
  indicator: IValuationIndicator;
}

export default function ValuationHistory({
  securityCountry,
  tickerSymbol,
  verbiageLanguage,
  indicator,
}: IValuationHistoryProps) {
  const [plotType, setPlotType] = useLocalStorage<"line" | "column">(PLOT_TYPE_STORAGE_KEY, "column", {
    initializeWithValue: false,
    deserializer: (value) => (JSON.parse(value) === "line" ? "line" : "column"),
  });
  const [axisScaleType, setAxisScaleType] = useLocalStorage<AxisScaleType>(AXIS_SCALE_TYPE_STORAGE_KEY, "linear", {
    initializeWithValue: false,
    deserializer: (value) => (JSON.parse(value) === "logarithmic" ? "logarithmic" : "linear"),
  });

  const intl = useIntl();
  const theme = useTheme();
  const selfie = useSelfie();
  const issuer = useRootIssuer();

  const { locale } = useRouter();
  const isUSALocale = locale?.startsWith("en") || false;
  const tickerSymbolsForSeries = [tickerSymbol];

  const { data, previousData, loading } = useQuery<
    IValuationIndicatorHistoryForTickerSymbolsQuery,
    IValuationIndicatorHistoryForTickerSymbolsQueryVariables
  >(ValuationIndicatorHistoryForTickerSymbolsQuery, {
    variables: {
      tickerSymbols: tickerSymbolsForSeries,
      securityCountry,
      indicator,
      verbiageLanguage,
      period: IValuationPeriod.YearEnd_10Fiscalyear,
    },
  });

  const [toggleBookmarkForIndicator, { loading: toggleBookmarkLoading, error }] = useMutation<
    IToggleBookmarkForIndicatorMutation,
    IToggleBookmarkForIndicatorMutationVariables
  >(ToggleBookmarkForIndicatorMutation, {
    variables: {
      indicator,
    },
  });
  const hasError = !!error;

  const valuationIndicatorHistoryForTickerSymbols =
    (loading && !data
      ? previousData?.valuationIndicatorHistoryForTickerSymbols
      : data?.valuationIndicatorHistoryForTickerSymbols) || [];

  if (loading && valuationIndicatorHistoryForTickerSymbols.length === 0) {
    return (
      <div className="contents">
        <div className="flex animate-pulse items-start justify-between pt-10">
          <div className="flex flex-col space-y-2">
            <span className="h-10 w-24 rounded-xl bg-slate-200 dark:bg-slate-700 md:w-32" />
            <div className="h-6 w-36 rounded-md bg-slate-200 dark:bg-slate-700 md:w-64" />
          </div>

          <div className="flex flex-col items-end space-y-2">
            <span className="flex h-16 w-24 rounded-lg bg-slate-200 p-1 dark:bg-slate-700 md:w-44" />

            <div className="flex h-2.5 w-12 items-center space-x-2 rounded-md bg-slate-200 dark:bg-slate-700 md:w-28" />
          </div>
        </div>

        <div className="mt-4 h-6 w-24 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />

        <div className="mt-4 animate-pulse space-y-2">
          <div className="h-2.5 w-full rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-2.5 w-full rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-2.5 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
        </div>

        <Separator className="mb-4 mt-7 bg-gray-100 dark:bg-slate-800" />

        <div className="flex animate-pulse justify-between">
          <div className="flex space-x-4">
            <div className="h-10 w-20 rounded-xl bg-slate-200 dark:bg-slate-700" />
            <div className="h-10 w-28 rounded-md bg-slate-200 dark:bg-slate-700" />
          </div>

          <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700" />
        </div>

        <div className="mt-4 h-60 w-full animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
      </div>
    );
  }

  if (valuationIndicatorHistoryForTickerSymbols.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <div>
            <SadFaceIcon />
          </div>

          {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Nenhum dado foi encontrado.</p>}
        </div>
      </div>
    );
  }

  const valuationData = valuationIndicatorHistoryForTickerSymbols.find(
    (node) => node.tickerSymbol === tickerSymbol.toUpperCase(),
  );

  if (!valuationData || valuationData.nodes.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <div>
            <SadFaceIcon />
          </div>

          {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Nenhum dado foi encontrado.</p>}
        </div>
      </div>
    );
  }

  const [mostRecent] = [...valuationData.nodes].sort((a, b) =>
    compareDesc(parseDateYmd(a.asOfDate), parseDateYmd(b.asOfDate)),
  );
  const isAdjusted = String(mostRecent.indicator).includes("NORMALIZED");
  const isAnnual = verifyIsAnnual(IValuationPeriod.YearEnd_10Fiscalyear, indicator);
  const isBookmarked = selfie ? selfie.settings.bookmarkedIndicators.includes(mostRecent.indicator) : false;

  // A logarithmic axis can't plot zero or negative values, so those fall back to linear regardless of the preference
  const canUseLogarithmicScale = valuationData.nodes.every((node) => node.value > 0);

  return (
    <div className="flex flex-col space-y-6 pt-10">
      <div className="flex items-start justify-between">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-2">
            <h2 className="m-0 text-3xl font-black text-slate-700 dark:text-slate-200 sm:text-4xl">
              {mostRecent.verbiage.abbreviation}
            </h2>
            {selfie !== null && (
              <div className="flex space-x-2">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      className={classNames(
                        "inline-flex h-8 w-8 items-center justify-center rounded-lg",
                        "bg-orange-50 text-orange-400 dark:bg-orange-900/30 dark:text-orange-300",
                        {
                          "bg-red-50 text-red-400 dark:bg-red-900/30 dark:text-red-300": hasError,
                          "pointer-events-none": toggleBookmarkLoading,
                        },
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleBookmarkForIndicator();
                      }}
                    >
                      {isBookmarked ? (
                        <BookmarkIcon fill="currentColor" className="fill-orange-400" />
                      ) : (
                        <BookmarkIcon />
                      )}
                    </TooltipTrigger>

                    <TooltipContent className="rounded-lg bg-gray-600 p-2 font-aeonik text-xs" side="top">
                      <span className="font-bold tracking-wider text-white">
                        {getLabelForBookmarkTooltip(isBookmarked, hasError, verbiageLanguage)}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          <h3 className="m-0 text-base font-medium -tracking-wide text-gray-400 dark:text-slate-400 sm:text-lg">
            {mostRecent.verbiage.formula}
          </h3>
        </div>

        <div className="flex flex-col items-end space-y-2">
          <div className="flex rounded-lg bg-violet-50 p-1 dark:bg-violet-900/20">
            <h4 className="flex rounded border border-violet-300 bg-white px-11 py-3.5 text-2xl font-bold text-violet-700 dark:border-violet-400 dark:bg-slate-800 dark:text-violet-300 sm:px-14 sm:py-4 sm:text-3xl">
              {mostRecent.formattedValue}
            </h4>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-[9px] font-medium italic text-gray-400 dark:text-slate-400">
              {intl.formatDate(parseDateYmd(mostRecent.asOfDate), {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })}
            </span>

            <div
              className={classNames("rounded-full bg-emerald-50 p-1.5 dark:bg-emerald-900/30", {
                "text-purple-50 dark:bg-purple-900/30": isAdjusted,
                "text-red-50 dark:bg-red-900/30": mostRecent.value < 0,
                "text-gray-50 dark:bg-gray-800": mostRecent.value === 0,
              })}
            >
              <div
                className={classNames("h-2 w-2 rounded-full bg-emerald-600 dark:bg-emerald-400", {
                  "bg-purple-600 dark:bg-purple-400": isAdjusted,
                  "bg-red-700 dark:bg-red-400": mostRecent.value < 0,
                  "bg-gray-400 dark:bg-gray-500": mostRecent.value === 0,
                })}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex w-full items-center justify-between">
          <div className="flex w-min rounded-md bg-orange-50 px-2 py-1 dark:bg-orange-900/30">
            <span className="flex text-[9px] font-bold uppercase text-orange-500 dark:text-orange-300">
              {isUSALocale ? "Description" : "Descrição"}
            </span>
          </div>

          <GraduationCapIcon className="h-5 w-5 text-gray-300 dark:text-slate-500" />
        </div>

        <ExpandableParagraph>{mostRecent.verbiage.description}</ExpandableParagraph>
      </div>

      <Separator className="bg-gray-100 dark:bg-slate-800" />

      {selfie && selfie.isPremium && valuationData.nodes.length >= 2 && (
        <>
          <div className="flex flex-col items-center justify-between sm:flex-row">
            <div className="flex w-full flex-col items-center gap-4 md:flex-row">
              <div className="flex w-full items-center gap-4 md:w-auto">
                <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />
                {canUseLogarithmicScale && <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />}
              </div>
            </div>

            <div className="hidden items-center space-x-3 sm:inline-flex">
              <div className="flex flex-col items-end">
                <span className="text-[9px] font-bold uppercase text-gray-400 dark:text-slate-400">
                  {isUSALocale ? "Median" : "Mediana"}
                </span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-200">
                  {valuationIndicatorFormatValue(
                    indicator,
                    calculateMedian(valuationData.nodes.map((node) => node.value)),
                  )}
                </span>
              </div>

              <div className="flex flex-col items-end">
                <span className="text-[9px] font-bold uppercase text-gray-400 dark:text-slate-400">
                  {isUSALocale ? "Mean" : "Média"}
                </span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-200">
                  {valuationIndicatorFormatValue(
                    indicator,
                    calculateMean(valuationData.nodes.map((node) => node.value)),
                  )}
                </span>
              </div>

              <SimpleTextTooltip content={issuer.name}>
                <Avatar
                  src={issuer.__typename !== "BrazilianRealEstateFund" && issuer.niceLogo ? issuer.niceLogo.url : null}
                  hashValue={issuer._id}
                  alt={issuer.name}
                  size={40}
                  squircle={true}
                />
              </SimpleTextTooltip>
            </div>
          </div>

          <div>
            <HighchartsChart
              className="h-56 w-full"
              options={getOptionsForChartHistory(
                valuationIndicatorHistoryForTickerSymbols,
                plotType,
                canUseLogarithmicScale ? axisScaleType : "linear",
                isAnnual,
                indicator,
                intl,
                theme,
              )}
            />
          </div>
        </>
      )}

      {selfie && selfie.isPremium && valuationData.nodes.length < 2 && (
        <div className="flex w-full items-center justify-center p-4 font-aeonik">
          <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
            <div>
              <SadFaceIcon />
            </div>

            {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Histórico indisponível.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function getOptionsForChartHistory(
  valuationIssuers: IValuationIndicatorHistoryForTickerSymbolsQuery["valuationIndicatorHistoryForTickerSymbols"],
  plotType: "line" | "column",
  axisScaleType: AxisScaleType,
  isAnnual: boolean,
  indicator: IValuationIndicator,
  intl: IntlShape,
  theme: "dark" | "light",
): Highcharts.Options {
  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const hoverBorderColor = theme === "dark" ? colors.slate[400] : colors.slate[500];
  const hoverFillColor = theme === "dark" ? colors.slate[700] : colors.slate[50];
  const dataLabelsColor = theme === "dark" ? colors.slate[300] : colors.slate[600];

  const series: (SeriesLineOptions | SeriesColumnOptions)[] = valuationIssuers.map((series, index) => {
    const colorTheme = getColorThemeForIndex(index, theme);

    if (!colorTheme) {
      throw new Error("Theme not found.");
    }

    const defaultOptions: SeriesLineOptions | SeriesColumnOptions = {
      showInNavigator: true,
      type: plotType,
      animation: false,
      name: series.tickerSymbol,
      color: colorTheme ? colorTheme.lineColor : undefined,
      lineWidth: 2,
      data: series.nodes
        .map((node) => {
          return {
            x: isAnnual ? getYear(parseISO(node.asOfDate)) : parseISO(node.asOfDate).getTime(),
            y: node.value,
            custom: node,
            label: node.formattedValue,
          };
        })
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: series.tickerSymbol,
        primaryColor: colorTheme.color,
        secondaryColor: colorTheme.lineColor,
      },
      dataLabels: {
        enabled: true,
        crop: false,
        style: {
          color: dataLabelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "600",
          fontSize: "10px",
          padding: "8px",
        },
        formatter() {
          return (this.point as any).label || "";
        },
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: colorTheme.color,
        color: colorTheme.color,
        lineColor: colorTheme.lineColor,
        symbol: "circle",
      },
    };

    if (plotType === "column") {
      const columnOptions = {
        borderColor: colorTheme ? colorTheme.lineColor : undefined,
        borderWidth: 1.5,
        states: {
          hover: {
            color: colorTheme ? colorTheme.lineColor : undefined,
          },
        },
      };
      return { ...columnOptions, ...defaultOptions };
    }

    return defaultOptions;
  });
  const minValues = valuationIssuers.map((series) => Math.min(...series.nodes.map((node) => node.value)));
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;
  const isPercentage = valuationIndicatorsFormatAsPercentage.includes(indicator);

  let adjustedMinValue = minValue;
  if (isPercentage && minValue === 0) {
    adjustedMinValue = -0.01;
  } else if (minValue === 0) {
    adjustedMinValue = -1;
  }

  return {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          if (isAnnual) {
            return String(this.value);
          }
          return intl.formatDate(new Date(this.value), {
            year: "2-digit",
            month: "numeric",
            day: "numeric",
          });
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      title: {
        text: "",
      },
      type: axisScaleType,
      min: adjustedMinValue,
      gridLineColor: slateOrDarkSlate,
      labels: {
        align: "center",
        enabled: true,
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter() {
          return valuationIndicatorFormatValue(indicator, parseFloat(String(this.value)));
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
      plotLines: valuationIssuers.slice(0, 1).map((series, index) => {
        const colorTheme = getColorThemeForIndex(index, theme);

        if (!colorTheme) {
          throw new Error("Theme not found.");
        }

        const median = calculateMedian(series.nodes.map((node) => node.value));

        return {
          value: median,
          color: colorTheme.lineColor,
          width: 1,
          zIndex: 5,
          dashStyle: "LongDash" as Highcharts.DashStyleValue,
        };
      }),
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter() {
        if (valuationIssuers.length === 0 || !this.points || this.points.length === 0) {
          return "";
        }

        const valuationValues = valuationIssuers[0].nodes;

        const node = (this.point as any).custom as (typeof valuationValues)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "ValuationIndicatorValue") {
          return "";
        }

        return renderToStaticMarkup(
          <div
            className="z-10 flex flex-col space-y-3 rounded-xl p-2 font-mono"
            style={{
              backgroundColor: tooltipBgColor,
              borderRadius: "12px",
            }}
          >
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {isAnnual
                ? String(getYear(new Date(node.asOfDate)))
                : intl.formatDate(parse(node.asOfDate, "yyyy-MM-dd", new Date()), {
                    year: "2-digit",
                    month: "numeric",
                    day: "numeric",
                  })}
            </span>

            <div className="space-y-1">
              {this.points
                .sort((a, b) => {
                  return (b.y || 0) - (a.y || 0);
                })
                .map(({ key, series, point }) => {
                  const node = (point as any).custom as (typeof valuationValues)[number] | null | undefined;
                  if (!key || !node || node.__typename !== "ValuationIndicatorValue") {
                    return "";
                  }

                  const color = String(series.options.custom?.primaryColor);
                  const lineColor = String(series.options.custom?.secondaryColor);

                  return (
                    <div key={color} className="flex items-center justify-between space-x-3">
                      <span
                        className="rounded-md px-2 py-1  font-mono text-xs font-bold"
                        style={{
                          backgroundColor: color,
                          color: lineColor,
                        }}
                      >
                        {series.name}
                      </span>

                      <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">
                        {node.formattedValue}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
      },
      line: {
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        threshold: null,
      },
      column: {
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };
}

function calculateMedian(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 !== 0) {
    return sorted[mid];
  }

  return (sorted[mid - 1] + sorted[mid]) / 2;
}

function calculateMean(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function verifyIsAnnual(period: IValuationPeriod, indicator: IValuationIndicator) {
  if (indicator === IValuationIndicator.PriceToFundsFromOperationsPerShare) {
    return false;
  }

  if (period === IValuationPeriod.YearEnd_10Fiscalyear || !verifyIndicatorIsValuationRatio(indicator)) {
    return true;
  }

  return false;
}
